/**
 * Utility class for handling keyboard events.
 */
export class KeyboardHelper {
    /**
     * Determines if the Enter key was pressed.
     * Note: `e.code` may be undefined on Android mobile devices.
     *
     * @param e - The keyboard event.
     * @returns True if the Enter key was pressed, false otherwise.
     */
    static isEnterKey(e) {
        return e.code === 'Enter' || e.key === 'Enter' || e.keyCode === 13;
    }
    /**
     * Checks if the Enter key was pressed without the Shift key.
     *
     * @param e - The keyboard event.
     * @returns True if only the Enter key was pressed, false otherwise.
     */
    static isEnterKeyWithoutShift(e) {
        return this.isEnterKey(e) && !e.shiftKey;
    }
    /**
     * Checks if an Enter press should submit: Enter without Shift that is not
     * part of an IME composition. keyCode 229 is treated as active composition
     * because Safari can leave `isComposing` unset on the keydown that confirms
     * an IME value.
     *
     * @param e - The React keyboard event.
     * @returns True if the Enter press should trigger a submit, false otherwise.
     */
    static isEnterSubmit(e) {
        return (this.isEnterKeyWithoutShift(e) && !e.nativeEvent.isComposing && e.nativeEvent.keyCode !== 229);
    }
    /**
     * Determines if the Arrow Down key was pressed.
     *
     * @param e - The keyboard event.
     * @returns True if the Arrow Down key was pressed, false otherwise.
     */
    static isArrowDown(e) {
        return e.key === 'ArrowDown';
    }
    /**
     * Determines if the Arrow Up key was pressed.
     *
     * @param e - The keyboard event.
     * @returns True if the Arrow Up key was pressed, false otherwise.
     */
    static isArrowUp(e) {
        return e.key === 'ArrowUp';
    }
    /**
     * Determines if the Escape key was pressed.
     *
     * @param e - The keyboard event.
     * @returns True if the Escape key was pressed, false otherwise.
     */
    static isEscapeKey(e) {
        return e.key === 'Escape';
    }
    /**
     * Determines if the Tab key was pressed.
     *
     * @param e - The keyboard event.
     * @returns True if the Tab key was pressed, false otherwise.
     */
    static isTabKey(e) {
        return e.key === 'Tab';
    }
    /**
     * Determines if the Slash key was pressed.
     *
     * @param e - The keyboard event.
     * @returns True if the Slash key was pressed, false otherwise.
     */
    static isOnlySlashKey(e) {
        return e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey;
    }
    static isCtrlOrCmdJ(e) {
        return (e.metaKey || e.ctrlKey) && e.key === 'j';
    }
}
//# sourceMappingURL=keyboardHelpers.js.map